import { ILibraryMainStore } from '../../../pages/mylibrary/typings/stores';
import { IFetchMatchesRequest, IFetchMatchesResponse } from '../../api/models/synonyms';
import SynonymsApiService from '../../api/synonymsApiService';
import { caption, captionStrong } from '../../styles/commonStyles';
import { ISafeExecuteAsyncAction } from '../../typings/stores';
import ModalWindow from './modalWindow';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Colors } from 'copyright-clearance-center-common-ui';
import { RupLoader } from 'copyright-clearance-center-common-ui-react';
import { inject, observer } from 'mobx-react';
import React from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			height: '15.25rem',
			rowGap: '0.75rem',
			overflowY: 'auto'
		},
		loader: {
			justifyContent: 'center'
		},
		keywordsLabel: {
			...captionStrong
		},
		keywords: {
			...caption,
			whiteSpace: 'break-spaces'
		},
		vocabularies: {
			display: 'flex',
			flexDirection: 'column',
			rowGap: '0.75rem'
		},
		vocabulary: {
			display: 'flex',
			flexDirection: 'row',
			gap: '0.25rem',
			alignItems: 'center'
		},
		rectangle: {
			width: '1rem',
			height: '1rem',
			borderRadius: '0.25rem',
			flexShrink: 0
		}
	})
);

export interface ISynonymsDlgProps {
	fullTextMatches: string;
	title: string;
	onHide: () => void;
	safeExecuteAsync?: ISafeExecuteAsyncAction;
	synonymsApiService?: SynonymsApiService;
}

export const SynonymsDlg: React.FunctionComponent<ISynonymsDlgProps> = props => {
	const classes = useStyles();
	const [isLoading, setIsLoading] = React.useState(true);
	const [matches, setMatches] = React.useState<IFetchMatchesResponse>();

	React.useEffect(() => {
		const fetch = async () => {
			const request = { fullTextMatches: props.fullTextMatches } as IFetchMatchesRequest;
			await props.safeExecuteAsync(async () => {
				const response = await props.synonymsApiService.fetchMatches(request);
				setMatches(response);
				setIsLoading(false);
			}, null);
		};

		fetch();
	}, []);

	const done = async () => {
		props.onHide();
	};

	const actions = [
		<button key="done" className="rup-btn rup-btn--lg rup-btn--contained" type="button" onClick={done}>
			{localize('Done')}
		</button>
	];

	const content = (
		<div className={clsx(classes.root, { [classes.loader]: isLoading })}>
			{isLoading ? (
				<RupLoader />
			) : (
				<>
					<span
						className={classes.keywordsLabel}
						style={{ color: Colors.neutral900, marginBottom: '0.25rem' }}
					>
						{localize('Keywords and Synonyms')}
					</span>
					{matches.keywords && matches.keywords.length > 0 && (
						<span className={classes.keywords}>{matches.keywords.join(', ')}</span>
					)}
					{matches.vocabularies && (
						<div className={classes.vocabularies}>
							{matches.vocabularies.map((vocabulary, index) => (
								<div key={index}>
									<div className={classes.vocabulary}>
										<div
											className={classes.rectangle}
											style={{ background: vocabulary.colorHash }}
										/>
										<span className={classes.keywordsLabel}>{vocabulary.name}:</span>
									</div>
									<span className={classes.keywords}>{vocabulary.keywords.join(', ')}</span>
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);

	const handleMouseClicks = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
	};

	return (
		<div onClick={handleMouseClicks} onDoubleClick={handleMouseClicks}>
			<ModalWindow
				title={props.title}
				width="31rem"
				content={content}
				actions={actions}
				closeHandler={done}
			></ModalWindow>
		</div>
	);
};

export default inject((stores: ILibraryMainStore) => ({
	synonymsApiService: stores.api.synonymsApiService,
	clearSelectedTags: stores.libraryFiltersStore.clearSelectedTags,
	safeExecuteAsync: stores.safeExecuteAsync
}))(observer(SynonymsDlg));
